import { useScienceTutorSection } from './scienceTutorContext';
import { Flame, Pause, Play, Snowflake } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Calorimeter3D } from './Calorimeter3D';
import { LIQUID_RANGE_C } from './calorimetryExplore';
import { formatSig } from './energyCalorimetry';
import { EnergyLevelDiagram, ThermometerReading } from './EnergyCalorimetryVisuals';
import {
  DEFAULT_PACK_SETUP,
  PACK_CHALLENGES,
  PACK_FIELDS,
  PACK_QUESTION,
  PACK_SUBSTANCES,
  calculatePackExplore,
  energyPerGram,
  type PackInput,
  type PackSetup,
} from './energyPacksModel';
import { ExploreChallenges } from './ExploreChallenges';
import { ModelInputPanel, WorkedStepsPanel } from './ModelInputs';

/**
 * Year 8 Explore of the Energy & Calorimetry Lab, "Hot pack and cold pack lab" (study-chemistry-tools-prd.md
 * v1.12, CT-INTERACT). The learner chooses a substance and types its mass, the water and the starting
 * temperature; one setup drives the worked steps, the two thermometers, the energy-level diagram and the
 * 3D calorimeter cup.
 */
export function EnergyPacksExplore() {
  const [setup, setSetup] = useState<PackSetup>(DEFAULT_PACK_SETUP);
  const [playing, setPlaying] = useState(true);
  useScienceTutorSection('EnergyPacksExplore', { setup });
  const result = useMemo(() => calculatePackExplore(setup), [setup]);
  const numbers: Record<PackInput, number> = { massG: setup.massG, waterMl: setup.waterMl, startC: setup.startC };

  const shownFinalC = result.finalC ?? setup.startC;
  const afterTone = result.finalC === null ? 'start' : result.direction === 'exothermic' ? 'warmer' : 'cooler';
  const sceneState = useMemo(
    () => ({ volumeMl: setup.waterMl, startTemperatureC: setup.startC, temperatureC: shownFinalC }),
    [setup.waterMl, setup.startC, shownFinalC],
  );
  const energyLabel = `${formatSig(result.energyKJ, 3)} kJ ${result.direction === 'exothermic' ? 'released' : 'taken in'}`;

  return (
    <div className="grid gap-6 xl:grid-cols-[340px_minmax(0,1fr)]">
      <aside className="grid content-start gap-4">
        <section className="rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">1. Choose a substance</p>
          <div className="mt-3 grid gap-2" aria-label="Substances">
            {PACK_SUBSTANCES.map((entry) => {
              const selected = entry.id === setup.substanceId;
              const Icon = entry.direction === 'exothermic' ? Flame : Snowflake;
              return (
                <button
                  key={entry.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setSetup((current) => ({ ...current, substanceId: entry.id }))}
                  className={`flex items-start gap-3 rounded-2xl border px-4 py-2.5 text-left ${selected ? 'border-ink bg-ink text-white' : 'border-hairline bg-surface text-ink'}`}
                >
                  <Icon
                    className={`mt-0.5 h-4 w-4 shrink-0 ${selected ? 'text-white' : entry.direction === 'exothermic' ? 'text-brand-coral' : 'text-brand-sky'}`}
                    aria-hidden="true"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-extrabold">{entry.name}</span>
                    <span className={`block text-xs font-medium leading-5 ${selected ? 'text-white/80' : 'text-slate2'}`}>
                      {entry.everyday} · {formatSig(energyPerGram(entry), 3)} kJ per gram
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </section>
        <ModelInputPanel
          legend="2. Set up your experiment"
          fields={PACK_FIELDS}
          values={numbers}
          onChange={(next) => setSetup((current) => ({ ...current, ...next }))}
          sliderKey="massG"
        />
        {/* Controls first (owner 2026-10-07: "调整的要放前面"), then the goals they tick. */}
        <ExploreChallenges question={PACK_QUESTION} challenges={PACK_CHALLENGES} result={result} />
      </aside>

      <section className="min-w-0 rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-xl font-extrabold text-ink">{result.substance.name} in water</h3>
            <p className="mt-1 break-words font-mono text-sm font-bold text-ink-soft">{result.substance.equation}</p>
          </div>
          <button
            type="button"
            aria-pressed={!playing}
            onClick={() => setPlaying((value) => !value)}
            className="inline-flex items-center gap-2 rounded-full border border-hairline bg-surface px-3 py-1.5 text-xs font-extrabold text-ink"
          >
            {playing ? <Pause className="h-4 w-4" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
            {playing ? 'Pause animation' : 'Play animation'}
          </button>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_200px]">
          <Calorimeter3D
            state={sceneState}
            playing={playing}
            label={`Insulated cup with ${formatSig(setup.waterMl, 3)} mL of water; the thermometer reads ${shownFinalC.toFixed(1)} °C`}
          />
          <div className="flex items-end justify-center gap-6 rounded-2xl bg-surface p-4">
            <ThermometerReading label="Before" temperatureC={setup.startC} tone="start" maxC={LIQUID_RANGE_C.max} />
            <ThermometerReading label="After" temperatureC={shownFinalC} tone={afterTone} maxC={LIQUID_RANGE_C.max} />
          </div>
        </div>

        <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)]">
          <WorkedStepsPanel
            title="How the model works it out"
            steps={result.steps}
            stepTestIdPrefix="packs-step"
            result={`Result: ${result.result}`}
            resultTestId="packs-result"
            limitNote={result.limitNote}
          />
          <div className="grid content-start gap-3">
            <EnergyLevelDiagram direction={result.direction} deltaHText={energyLabel} />
            <p className="rounded-2xl bg-surface p-3 text-xs font-bold leading-5 text-slate2">
              {result.substance.changeType === 'chemical'
                ? 'This is a chemical reaction: new substances (magnesium chloride and hydrogen gas) form.'
                : 'Dissolving is a physical change: the solid comes back if the water evaporates. It still moves energy.'}{' '}
              Data: {result.substance.source}. Pending teaching review.
            </p>
          </div>
        </div>
        <p className="mt-4 text-xs font-medium leading-5 text-slate2">This Explore view does not record progress.</p>
      </section>
    </div>
  );
}
